import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Pagination,
  Center,
  Paper,
  Drawer,
  Divider,
  Group
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import axios from 'axios';
import Can from '../components/Can';
import RequestsTable from "../components/RequestsTable";
import Request from './Request';
import CheckboxButton from '../components/CheckboxButton';
import { mtsUrl } from '../lib/mts-url';

import './Requests.css';
import { useRequests, useRequestsDispatch } from '../reducers/requestsReducer';

const btos = (val: any) => !!val ? '1' : '0';

const requestsUrl = (
  filter: string,
  activePage: number,
  hideInactive: boolean,
  assignedToMe: boolean,
  sort?: string | null,
) => {
  const params = [
    `requests.json?filter=${filter}`,
    `page=${activePage}`,
    `hc=${btos(hideInactive)}`,
    `atm=${btos(assignedToMe)}`,
  ];
  
  if (sort) params.push(`sort=${sort}`);
  
  return mtsUrl(params.join('&'));
};

interface RequestsProps {
  requestOpen?: boolean;
}

export default function Requests(
  { requestOpen = false }: RequestsProps
) {
  const navigate = useNavigate();
  const dispatch = useRequestsDispatch();
  const {
    sort,
    totalPages,
    fetching,
    filter,
    filterOptions: { hideInactive, assignedToMe },
  } = useRequests();
  const { page, requestId } = useParams();
  const showRequest = !!(requestOpen && requestId);
  const activePage = page ? parseInt(page, 10) : 1;
  const [searchParams, setSearchParams] = useSearchParams();
  let searchFilter = searchParams.has('filter') ? searchParams.get('filter') : null;
  if (!searchFilter) searchFilter = 'all';
  const query = useQuery({
    queryKey: ['requests', searchFilter, sort, activePage, hideInactive, assignedToMe],
    queryFn: () => axios.get(requestsUrl(searchFilter, activePage, hideInactive, assignedToMe, sort)),
  });
  const location = useLocation();
  const locationRef = useRef(location);
  const onPageChange = useCallback((newPage: number) =>
    navigate(`/requests/page/${newPage}?filter=${filter}`),
    [filter]
  );

  const onDrawerClose = useCallback(() => {
    const { pathname, search } = locationRef.current;

    if (pathname.substring(0, 9) === '/requests')
      navigate(pathname + search);
    else
      navigate('/requests');
  }, []);

  if (!requestOpen) {
    locationRef.current = location;
  }

  useEffect(() => {
    if (!showRequest && filter !== searchFilter)
      dispatch({ type: 'SET_FILTER', filter: searchFilter });
  }, [searchFilter, showRequest]);

  useEffect(() => {
    if (!fetching && query.isPending)
      dispatch({ type: 'FETCH_REQUESTS' });
  }, [fetching, query.isPending]);

  useEffect(() => {
    if (!query.data || showRequest) return;
    const { items, total_pages } = query.data.data;

    dispatch({
      type: 'SET_REQUESTS',
      requests: items,
      totalPages: total_pages
    });
  }, [query.data, showRequest]);

  return (
    <>
      <Paper radius={6} mt="lg" withBorder>
        <Group p={12}>
          <CheckboxButton
            label="Hide inactive"
            checked={hideInactive}
            onChange={(c: boolean) =>
              dispatch({ type: 'SET_HIDE_INACTIVE', hideInactive: c })
            }
          />
          <Can do="update_assignment" on="Request">
            <CheckboxButton
              label="Assigned to me"
              checked={assignedToMe}
              onChange={(c: boolean) =>
                dispatch({ type: 'SET_ASSIGNED_TO_ME', assignedToMe: c })
              }
            />
          </Can>
        </Group>
        <RequestsTable onRowClick={r => navigate(r.show_path)} />
        <Divider size="xs" />
        <Center h={100}>
          <Pagination total={totalPages} value={activePage} onChange={onPageChange} />
        </Center>
      </Paper>
      <Drawer
        opened={showRequest}
        onClose={onDrawerClose}
        withCloseButton={false}
        position="right"
        size="80vw"
      >
        {showRequest && <Request onClose={onDrawerClose} />}
      </Drawer>
    </>
  );
}
